import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Card, Col, Row} from "reactstrap";
import {AvField, AvForm} from 'availity-reactstrap-validation';

import {MAX_NOTE, MIN_NOTE} from 'constants/lengthValidity';
import {strWithNoOnlySpaces} from 'constants/validityPatterns';
import AppointmentsService from 'services/appointments';
import CallsService from 'services/calls';
import {
    ACCEPT_STATUS,
    FIT_MODAL,
    FIT_NOTE_AGREED_TYPE,
    PRESCRIPTION_AGREED_TYPE,
    PRESCRIPTION_MODAL,
    REFERAL_AGREED_TYPE,
    DAYS_IN_WEEK,
    FOLLOW_UP_DURATIONS,
    MENTAL_HEALTH_PLAN,
    MIN_FOLLOW_UP_LEAD_DAYS,
    OTHER_DURATION,
    REFERRAL_MODAL,
    sickArray
} from 'constants/callConstants';
import {SICK_IDENTIFICATION} from 'constants/documentStatuses';
import generateBitmask from 'helpers/generateBitmask';
import {NOTES_VARIABLES} from 'constants/storageConstants';
import {BEFORE_UNLOAD} from 'constants/windowActions';
import StorageService from 'services/StorageService';
import {AutoSizeTextArea} from "components/form/AutoSizeTextArea";
import toastr from 'components/toastr';
import {addUkDays, displayUkDate, ukDateToInstant} from 'helpers/ukDate';
import {consultationNotesFormFields} from "constants/appointment";

import CreateReferralLetter from '../letters/referralLetter';
import Prescription from '../letters/prescription';
import CreateSickLetter from '../letters/fitSickNote';

export default function ConsultationNotes({
    formRef,
    setShowFeedbackModal,
    appointment,
    setIsAgreedToShareInfo,
    documentsToFill,
    setIsSubmitting,
}) {

    const savedValues = new StorageService().getValueByKey(NOTES_VARIABLES);

    const [showDocumentModal, setShowDocumentModal] = useState('');
    const [consultationNotes, setConsultationNotes] = useState(savedValues?.notes || {
        problem: "",
        history: "",
        examination: "",
        pastMedicalHX: "",
        comments: "",
        safetyNet: "",
    });

    const agreedDocs = useMemo(() => {
        if (savedValues?.docs.length) {
            return [...savedValues?.docs, ...documentsToFill]
        }
        return documentsToFill
    }, [savedValues, documentsToFill])

    // The mental-health question. Asked of EVERY consultation, and the answer belongs to this
    // consultation alone - a patient may be "not mental health" today and "mental health" next
    // month, and both records stay true of their own consultation. Deliberately starts unanswered
    // rather than defaulting to "no", so an unanswered question is distinguishable from a negative.
    // The submit button lives in the other column, so an error rendered down here is easy to miss.
    // Holding a ref lets a refused submit bring the question back into view instead of appearing to
    // do nothing at all.
    const mentalHealthRef = useRef(null);
    const [isMentalHealth, setIsMentalHealth] = useState(null);
    const [mentalHealthPlan, setMentalHealthPlan] = useState(null);
    // The clinician picks a DURATION, not a date. followUpWeeks holds 1, 2, 4 or OTHER_DURATION;
    // customFollowUpDate is only used for the last of those. The actual due date is derived at
    // submit time and never shown - when the reminder fires is the system's business, not a number
    // for the clinician to reason about mid-consultation.
    const [followUpWeeks, setFollowUpWeeks] = useState(null);
    const [customFollowUpDate, setCustomFollowUpDate] = useState('');
    const [mentalHealthError, setMentalHealthError] = useState('');

    const [referralID, setReferralID] = useState(null);
    const [sickID, setSickID] = useState(null);
    const [prescriptionID, setPrescriptionID] = useState(null);

    const patientID = useMemo(() => appointment?.patient.id, [appointment]);

    const documentsOperations = {
        [REFERAL_AGREED_TYPE]: () => setShowDocumentModal(REFERRAL_MODAL),
        [FIT_NOTE_AGREED_TYPE]: () => setShowDocumentModal(FIT_MODAL),
        [PRESCRIPTION_AGREED_TYPE]: () => setShowDocumentModal(PRESCRIPTION_MODAL),
    };

    const errorMessage = `Must be from ${MIN_NOTE} to ${MAX_NOTE} symbols and cannot contain spaces only`
    const validate = {
        required: { value: true, errorMessage: errorMessage },
        pattern: { value: strWithNoOnlySpaces, errorMessage: errorMessage },
        minLength: { value: MIN_NOTE, errorMessage: errorMessage },
        maxLength: { value: MAX_NOTE, errorMessage: errorMessage }
    }

    function* openDocumentsModals(documentsArray) {
        for (let modalCounter = 0; modalCounter <= documentsArray.length - 1; modalCounter++) {
            yield documentsOperations[documentsArray[modalCounter]]();
        }
        return setShowFeedbackModal(true)
    }

    /* eslint-disable-next-line */
    const documentSequence = useMemo(() => openDocumentsModals(agreedDocs), [documentsToFill]);

    async function sendReferralLetter(values) {
        const newValues = {
            ...values,
            appointmentSlotId: appointment.id,
        }
        try {
            await CallsService.sendReferralLetter(newValues);
            setShowDocumentModal('');
            documentSequence.next();
        } finally {
            //empty
        }
    }

    async function sendSickLetter({ type, checked, ...other }) {
        const itemsForBitmask = sickArray.map((item) => !!checked.includes(item))

        const newValues = {
            ...other,
            benefits: generateBitmask(itemsForBitmask),
            isFitNote: type !== SICK_IDENTIFICATION,
            appointmentSlotId: appointment.id
        }
        try {
            await CallsService.sendSickLetter(newValues);
            setShowDocumentModal('');
            documentSequence.next();
        } finally {
            //empty
        }
    }

    async function sendPrescriptionLetter(drugs) {
        const values = {
            drugs: [...drugs],
            appointmentSlotId: appointment.id,
        }
        try {
            await CallsService.sendPrescriptionLetter(values);
            setShowDocumentModal('');
            documentSequence.next();
        } finally {
            //empty
        }
    }

    /**
     * Turn the chosen duration into the date the backend wants.
     *
     * @returns {string} an ISO date-time, or '' when nothing is chosen yet
     */
    const earliestFollowUpDate = addUkDays(MIN_FOLLOW_UP_LEAD_DAYS);

    function resolveFollowUpDate() {
        if (followUpWeeks === OTHER_DURATION) {
            return customFollowUpDate;
        }

        return followUpWeeks ? addUkDays(followUpWeeks * DAYS_IN_WEEK) : '';
    }

    function resolveFollowUpDueAt() {
        return ukDateToInstant(resolveFollowUpDate());
    }

    /**
     * The mental-health answer must be complete before anything is submitted.
     *
     * The backend requires a plan whenever the answer is yes, and a date whenever the plan is a
     * follow-up. Catching that here rather than letting the request 400 matters because the
     * consultation notes save FIRST - without this the clinician would see their notes save and the
     * mental-health answer fail, with no obvious way to tell which half went wrong.
     *
     * @returns {string} an error to show, or '' when the answer is complete or not given
     */
    function validateMentalHealthAnswer() {
        // Mandatory, like the note fields beside it. Every consultation gets an answer, so that a
        // blank one is never mistaken for a considered "no".
        if (isMentalHealth === null) {
            return 'Please answer whether this is a mental health consultation.';
        }

        if (isMentalHealth !== true) {
            return '';
        }

        if (!mentalHealthPlan) {
            return 'Choose a follow-up plan.';
        }

        if (mentalHealthPlan === MENTAL_HEALTH_PLAN.followUp) {
            const followUpDate = resolveFollowUpDate();

            if (!followUpDate) {
                return followUpWeeks === OTHER_DURATION
                    ? 'Choose a follow-up date.'
                    : 'Choose when the follow-up is due.';
            }

            // The patient is emailed two days before the due date. A date sooner than that puts the
            // reminder in the past, and the scheduler sends it on its very next run.
            if (followUpDate < earliestFollowUpDate) {
                return `Choose a follow-up date from ${displayUkDate(earliestFollowUpDate)} onwards. `
                    + 'The patient is emailed two days beforehand, so an earlier date would send '
                    + 'that email immediately.';
            }
        }

        return '';
    }

    async function handleSubmitForm(event, { problem, history, pastMedicalHX, examination, comments, safetyNet }) {
        const mentalHealthProblem = validateMentalHealthAnswer();

        if (mentalHealthProblem) {
            setMentalHealthError(mentalHealthProblem);

            // The button that was just pressed is in the other column, and this message renders at
            // the bottom of a 500px scroll box - so without this the screen appears to ignore the
            // click entirely, and the clinician presses it again and again.
            toastr.error(mentalHealthProblem);

            if (mentalHealthRef.current) {
                mentalHealthRef.current.scrollIntoView({ block: 'center', behavior: 'smooth' });

                const firstOption = mentalHealthRef.current.querySelector('input[type="radio"]');

                if (firstOption) {
                    firstOption.focus();
                }
            }

            return;
        }

        setMentalHealthError('');
        setIsSubmitting(true);

        const notes = {
            problem,
            history,
            pastMedicalHX,
            examination,
            comments,
            safetyNet
        };
        setConsultationNotes(notes);

        if (agreedDocs.includes(REFERAL_AGREED_TYPE)) {
            // This branch is unreachable today - AGREED_REFERRAL and AGREED_FIT_NOTE are commented
            // out of AGREED_DOCUMENTS - and the consultation notes it skips are handled by the
            // pre-existing referral flow, which is not ours to restructure. But the question above
            // is mandatory, so store the answer before advancing rather than collecting it and
            // throwing it away should referrals ever be turned back on.
            if (await saveMentalHealthReview()) {
                documentSequence.next();
            }

            setIsSubmitting(false);

            return;
        }
        try {
            const newNotes = {
                problem: notes.problem,
                history: notes.history,
                pastMedicalHX: notes.pastMedicalHX,
                examination: notes.examination,
                comments: notes.comments,
                safetyNet: notes.safetyNet,
            }
            await AppointmentsService.sendConsultation(appointment.id, newNotes);

            // Only move on once the answer is stored. documentSequence.next() opens the feedback
            // modal, which covers this whole card and then navigates to /appointments on submit -
            // so advancing after a failed save would hide the error and lose the answer with it.
            if (await saveMentalHealthReview()) {
                documentSequence.next();
            }
        } catch {
            //empty
        } finally {
            setIsSubmitting(false);
        }
    }

    /**
     * Record the mental-health answer for this consultation.
     *
     * Sends exactly what was chosen: the backend rejects a plan supplied alongside an answer of
     * "no", and requires a date for a follow-up but forbids one for a repeat plan.
     *
     * A failure here must NOT lose the consultation notes, which have already saved successfully -
     * so it is caught rather than thrown. It returns whether the answer was stored, because the
     * caller must not advance into the feedback modal on a failure: that modal covers this card and
     * then navigates away, which would take the error with it. Skipped entirely when the question
     * was left unanswered, because an unanswered question is not the same as an answer of "no" and
     * should not be recorded as one.
     *
     * @returns {Promise<boolean>} true when the answer is stored (or there was none to store)
     */
    async function saveMentalHealthReview() {
        // Unreachable now the question is mandatory, but kept: it is the one state that must never
        // be recorded as an answer, and a future change to the form should not be able to.
        if (isMentalHealth === null) {
            return true;
        }

        const review = { isMentalHealth };

        if (isMentalHealth && mentalHealthPlan) {
            review.plan = mentalHealthPlan;

            if (mentalHealthPlan === MENTAL_HEALTH_PLAN.followUp) {
                review.followUpDueAt = resolveFollowUpDueAt();
            }
        }

        try {
            await AppointmentsService.setMentalHealthReview(appointment.id, review);

            return true;
        } catch (error) {
            const message = 'The consultation notes saved, but the mental health answer did not. '
                + 'Please try again before leaving this page.';

            setMentalHealthError(message);
            toastr.error(message);

            if (mentalHealthRef.current) {
                mentalHealthRef.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
            }

            return false;
        }
    }

    async function sendRequestForReferral() {
        try {
            const { data } = await CallsService.sendReferralRequest({ appointmentSlotId: appointment.id });
            setReferralID(data.id)
        } finally {
            //empty
        }
    }

    async function sendRequestToFitNote() {
        try {
            const { data } = await CallsService.sendFitRequest({ appointmentSlotId: appointment.id })
            setSickID(data.id)
        } finally {
            //empty
        }
    }

    async function sendRequestForPrescription() {
        try {
            const { data } = await CallsService.sendPrescriptionRequest({ appointmentSlotId: appointment.id })
            setPrescriptionID(data.id)
            if (data.status === ACCEPT_STATUS) {
                setIsAgreedToShareInfo(true);
            }
        } finally {
            //empty
        }
    }

    const saveValuesHandler = useCallback(
        () => {
            new StorageService().addValueByKey(NOTES_VARIABLES, {
                notes: consultationNotes,
                docs: agreedDocs,
                refID: savedValues?.refID ? savedValues.refID : referralID,
                sickID: savedValues?.sickID ? savedValues.sickID : sickID,
                presID: savedValues?.presID ? savedValues.presID : prescriptionID,
            });
        },
        [consultationNotes, agreedDocs, prescriptionID, sickID, referralID, savedValues],
    )

    function handleChangeConsultationValues(value, fieldName) {
        setConsultationNotes(prevState => (
            {
                ...prevState,
                [fieldName]: value,
            }
        ))
    }

    useEffect(() => {
        window.addEventListener(BEFORE_UNLOAD, saveValuesHandler);
        return () => {
            window.removeEventListener(BEFORE_UNLOAD, saveValuesHandler);
        }
    }, [saveValuesHandler]);

    return (
        <Card className="call-consultation-notes custom-design-scroll">
            <label className="consultation-label">Consultation notes</label>
            <AvForm onValidSubmit={handleSubmitForm} ref={formRef}>
                <div className="call-consultation-notes__inputs-section custom-design-scroll">
                    {consultationNotesFormFields.map(note => (
                        <React.Fragment key={note.name}>
                            <AvField
                                type="textarea"
                                rows={1}
                                name={note.name}
                                label={note.label}
                                placeholder="Additional comment"
                                validate={validate}
                                onChange={(e, v) => handleChangeConsultationValues(v, note.name)}
                                value={consultationNotes[note.name] || ""}
                                className="form-control"
                                tag={AutoSizeTextArea}
                            />
                        </React.Fragment>
                    ))}

                    <div className="mh-section" ref={mentalHealthRef}>
                        <span className="consultation-label mh-section__question">
                            Is this a mental health consultation?
                        </span>

                        <div className="mh-options" role="radiogroup" aria-label="Mental health consultation">
                            {[{ value: false, label: 'No' }, { value: true, label: 'Yes' }].map(opt => (
                                <label
                                    key={String(opt.value)}
                                    className={`mh-option${isMentalHealth === opt.value ? ' mh-option--selected' : ''}`}
                                >
                                    <input
                                        type="radio"
                                        name="isMentalHealth"
                                        checked={isMentalHealth === opt.value}
                                        onChange={() => {
                                            setIsMentalHealth(opt.value);
                                            setMentalHealthError('');

                                            if (!opt.value) {
                                                setMentalHealthPlan(null);
                                                setFollowUpWeeks(null);
                                                setCustomFollowUpDate('');
                                            }
                                        }}
                                    />
                                    <span>{opt.label}</span>
                                </label>
                            ))}
                        </div>

                        {isMentalHealth && (
                            <>
                                <span className="consultation-label mh-section__question">Follow-up plan</span>

                                <div className="mh-options" role="radiogroup" aria-label="Follow-up plan">
                                    {[
                                        { value: MENTAL_HEALTH_PLAN.followUp, label: 'Follow-up needed' },
                                        { value: MENTAL_HEALTH_PLAN.repeatPlan, label: 'Join repeat prescription plan' },
                                    ].map(opt => (
                                        <label
                                            key={opt.value}
                                            className={`mh-option mh-option--wide${mentalHealthPlan === opt.value ? ' mh-option--selected' : ''}`}
                                        >
                                            <input
                                                type="radio"
                                                name="mentalHealthPlan"
                                                checked={mentalHealthPlan === opt.value}
                                                onChange={() => {
                                                    setMentalHealthPlan(opt.value);
                                                    setMentalHealthError('');

                                                    if (opt.value !== MENTAL_HEALTH_PLAN.followUp) {
                                                        setFollowUpWeeks(null);
                                                        setCustomFollowUpDate('');
                                                    }
                                                }}
                                            />
                                            <span>{opt.label}</span>
                                        </label>
                                    ))}
                                </div>
                            </>
                        )}

                        {isMentalHealth && mentalHealthPlan === MENTAL_HEALTH_PLAN.repeatPlan && (
                            <small className="text-muted mh-section__note">
                                The patient is emailed an invitation to the repeat prescription
                                service three weeks after today.
                            </small>
                        )}

                        {isMentalHealth && mentalHealthPlan === MENTAL_HEALTH_PLAN.followUp && (
                            <>
                                <span className="consultation-label mh-section__question">Follow up in</span>
                                <small className="text-muted mh-section__note">
                                    The patient is emailed a reminder two days before this date.
                                </small>

                                <div className="mh-options" role="radiogroup" aria-label="Follow up in">
                                    {FOLLOW_UP_DURATIONS.map(opt => (
                                        <label
                                            key={String(opt.value)}
                                            className={`mh-option${followUpWeeks === opt.value ? ' mh-option--selected' : ''}`}
                                        >
                                            <input
                                                type="radio"
                                                name="followUpWeeks"
                                                checked={followUpWeeks === opt.value}
                                                onChange={() => {
                                                    setFollowUpWeeks(opt.value);
                                                    setMentalHealthError('');

                                                    if (opt.value !== OTHER_DURATION) {
                                                        setCustomFollowUpDate('');
                                                    }
                                                }}
                                            />
                                            <span>{opt.label}</span>
                                        </label>
                                    ))}
                                </div>

                                {followUpWeeks === OTHER_DURATION && (
                                    <input
                                        className="form-control mh-section__date"
                                        type="date"
                                        aria-label="Follow-up date"
                                        value={customFollowUpDate}
                                        min={earliestFollowUpDate}
                                        onChange={e => {
                                            setCustomFollowUpDate(e.target.value);
                                            setMentalHealthError('');
                                        }}
                                    />
                                )}
                            </>
                        )}

                        {mentalHealthError && (
                            <div className="mh-section__error" role="alert">{mentalHealthError}</div>
                        )}
                                    </div>
                </div>
                <Row className="d-flex flex-wrap">
                    <Col lg={4}>
                        <button className="btn btn-primary call-form-button w-100" type="button"
                                onClick={sendRequestForReferral}>Create Referral payment
                        </button>
                    </Col>
                    <Col lg={4}>
                        <button className="btn btn-primary call-form-button w-100" type="button"
                                onClick={sendRequestToFitNote}>Create Sick payment
                        </button>
                    </Col>
                    <Col lg={4}>
                        <button type="button" className="btn btn-primary call-form-button w-100"
                                onClick={sendRequestForPrescription}>Create Prescription
                        </button>
                    </Col>
                </Row>
            </AvForm>
            {showDocumentModal === REFERRAL_MODAL &&
                <CreateReferralLetter
                    handleClose={() => setShowDocumentModal('')}
                    appointment={appointment}
                    consultationNotes={consultationNotes}
                    referralID={savedValues?.refID ? savedValues.refID : referralID}
                    patientID={patientID}
                    saveReferralLetter={sendReferralLetter}
                />}
            {showDocumentModal === PRESCRIPTION_MODAL &&
                <Prescription
                    handleClose={() => setShowDocumentModal('')}
                    prescriptionID={savedValues?.presID ? savedValues.presID : prescriptionID}
                    patientID={patientID}
                    sendPrescriptionLetter={sendPrescriptionLetter}
                    appointment={appointment}
                />}
            {showDocumentModal === FIT_MODAL &&
                <CreateSickLetter
                    handleClose={() => setShowDocumentModal('')}
                    sickID={savedValues?.sickID ? savedValues.sickID : sickID}
                    patientID={patientID}
                    sendSickLetter={sendSickLetter}
                    appointment={appointment}
                />}
        </Card>
    )
}
